import { Component, DestroyRef, inject, OnInit, signal, Type } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';

interface ImageOption {
  value: string;
  label: string;
  url: string;
}
export interface ImageSelectorProps extends FormlyFieldProps {
  options?: ImageOption[];
}

export interface FormlyImageSelectorFieldConfig extends FormlyFieldConfig<ImageSelectorProps> {
  type: 'image-selector' | Type<ImageSelectorComponent>;
}

@Component({
  selector: 'vectronic-formly-image-selector',
  imports: [MatIcon],
  template: `
    <div class="image-selector">
      <p class="title">
        {{ props.label }}
        @if (props.required) {
          <span>*</span>
        }
      </p>
      <div
        class="grid"
        [id]="field.id"
        tabindex="-1"
        [class.pulsating]="pulsating()"
        (mousedown)="onGridMouseDown($event)"
        (focus)="onGridFocus()"
      >
        @for (image of props.options; track image.value) {
          <div
            class="image-card"
            [class.selected]="selectedImage() === image.value"
            [class.disabled]="formControl.disabled"
            (click)="selectImage(image.value)"
          >
            <img [src]="image.url" />
            <div>{{ image.label }}</div>
            @if (selectedImage() === image.value) {
              <mat-icon class="checkmark">check_circle</mat-icon>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: `
    @use 'variables';
    $accent-color: #2b4265;

    .image-selector {
      max-width: 800px;
      margin: 0 auto;
      padding: 1.5rem 0;
      margin-bottom: 1.25rem;
    }
    .image-selector.disabled {
      opacity: 0.6;
      pointer-events: none;
    }

    .title {
      padding-left: 1rem;
      color: #333;
      margin-bottom: 1rem;
      font-size: 1.25rem;
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 1rem;
    }

    .grid.pulsating {
      animation: pulseEffect 0.75s ease-in-out 2;
    }

    .image-card {
      max-width: 200px;
      position: relative;
      border-radius: 12px;
      cursor: pointer;
      border: 1px solid #ccc;
      transition: all 0.3s ease;
      display: flex;
      flex-direction: column;
      justify-content: center;
      place-items: center;
      gap: 0.5rem;
      padding-bottom: 0.75rem;
    }

    .image-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    }

    .image-card.disabled {
      cursor: default;
      filter: grayscale(50%);
    }

    .image-card.disabled:hover {
      transform: none;
      box-shadow: none;
    }

    .image-card.selected {
      border-color: $accent-color;
    }
    .image-card.disabled.selected {
      border-color: #9ca3af;
    }

    .image-card img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .checkmark {
      position: absolute;
      top: 8px;
      right: 8px;
      color: $accent-color;
      font-size: 1.5rem;
    }
    .image-card.disabled .checkmark {
      color: #9ca3af;
    }

    @keyframes pulseEffect {
      0% {
        box-shadow: 0 0 0 0 variables.$vectronic-danger;
      }

      100% {
        box-shadow: 0 0 0 10px rgba(variables.$vectronic-danger, 0);
      }
    }
  `,
})
export class ImageSelectorComponent
  extends FieldType<FieldTypeConfig<ImageSelectorProps>>
  implements OnInit
{
  private destroyRef = inject(DestroyRef);
  selectedImage = signal<string | null>(null);
  pulsating = signal(false);

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.selectedImage.set(value));
  }

  selectImage(value: string) {
    if (this.formControl.disabled) {
      return;
    }
    this.formControl.setValue(value);
  }

  onGridMouseDown(event: MouseEvent) {
    event.preventDefault();
    (document.activeElement as HTMLElement)?.blur();
  }

  onGridFocus() {
    this.pulsating.set(true);
    setTimeout(() => this.pulsating.set(false), 1500);
  }
}
